import type { Key } from "react";
import { useEffect, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
} from "@headlessui/react";
import { twMerge } from "tailwind-merge";

import { Label } from "~/ui";
import { Spinner } from "~/ui/Spinner";
import { CheckIcon, ChevronDownIcon, XMarkIcon } from "../Icons";

export interface SearchableMultiSelectOption<T> {
  label: string;
  id: T;
}

export interface SearchableMultiSelectProps<T> {
  id: string;
  options: SearchableMultiSelectOption<T>[];
  placeholder?: string;
  label?: string;
  value?: T[];
  onChange: (v: T[]) => void;
  showSelectedAsBadges?: boolean;
  onSearch?: (query: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  selectedItems?: SearchableMultiSelectOption<T>[];
}

export const SearchableMultiSelect = <T extends Key | null | undefined>({
  id,
  options,
  placeholder,
  label,
  value = [],
  onChange,
  onSearch,
  isLoading,
  emptyMessage,
  selectedItems,
}: SearchableMultiSelectProps<T>) => {
  const [query, setQuery] = useState("");
  const [selectedOptions, setSelectedOptions] =
    useState<SearchableMultiSelectOption<T>[]>();

  // Helper to toggle selection
  const toggleOption = (option: SearchableMultiSelectOption<T>) => {
    if (!option) return;
    if (value.includes(option.id)) {
      // Remove the option
      const newValue = value.filter((v) => v !== option.id);
      const newSelectedOptions =
        selectedOptions?.filter((o) => o.id !== option.id) ?? [];
      onChange(newValue);
      setSelectedOptions(newSelectedOptions);
    } else {
      // Add the option
      const newValue = [...value, option.id];
      const newSelectedOptions = [...(selectedOptions ?? []), option];
      onChange(newValue);
      setSelectedOptions(newSelectedOptions);
    }
  };

  // Helper to remove a selected option
  const removeOption = (id: T) => {
    const newValue = value.filter((v) => v !== id);
    const newSelectedOptions =
      selectedOptions?.filter((o) => o.id !== id) ?? [];
    onChange(newValue);
    setSelectedOptions(newSelectedOptions);
  };

  // Sync selectedOptions with selectedItems when they change
  useEffect(() => {
    if (selectedItems && selectedItems.length > 0) {
      setSelectedOptions(selectedItems);
    } else {
      setSelectedOptions([]);
    }
  }, [selectedItems]);

  return (
    <Field>
      {label && (
        <Label htmlFor={id} className={twMerge("mb-2")}>
          {label}
        </Label>
      )}

      {selectedOptions && selectedOptions.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <div
              key={option?.id ?? "empty"}
              className="flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1.5 text-sm text-white"
            >
              <span>{option.label}</span>
              <button
                type="button"
                onClick={() => removeOption(option.id)}
                className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-700 transition-colors hover:bg-blue-800"
                aria-label={`Remove ${option.label}`}
              >
                <XMarkIcon className="h-3 w-3" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Combobox value={selectedOptions} multiple onClose={() => setQuery("")}>
        <div className="relative">
          <ComboboxInput
            id={id}
            className={twMerge(
              "w-full rounded-lg border border-neutral-variant-60 bg-surface-container-low px-4 py-3.5 text-left text-secondary-20 placeholder-neutral-variant-60 dark:border-neutral-variant-40 dark:bg-surface-container-low-dark dark:text-secondary",
            )}
            placeholder={placeholder}
            onChange={(event) => {
              const newQuery = event.target.value;
              setQuery(newQuery);
              onSearch?.(newQuery);
            }}
            value={query}
          />
          <ComboboxButton className="group absolute inset-y-0 right-2 px-2.5">
            {isLoading ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <ChevronDownIcon
                className="size-4 text-secondary-20 dark:text-secondary"
                strokeWidth={3}
                aria-hidden="true"
              />
            )}
          </ComboboxButton>
        </div>

        <ComboboxOptions
          anchor="bottom"
          className={twMerge(
            "z-20 mt-3 max-h-combobox-values w-[var(--input-width)] overflow-y-auto rounded-2xl [--anchor-gap:var(--spacing-1)]",
            options.length > 3 && "h-combobox-values",
          )}
        >
          {isLoading ? (
            <div className="flex items-center justify-center bg-surface-container-low p-8 dark:bg-surface-container-low-dark">
              <Spinner className="h-6 w-6" />
            </div>
          ) : options.length === 0 ? (
            <div className="flex items-center justify-center bg-surface-container-low p-8 dark:bg-surface-container-low-dark">
              <p className="text-sm text-neutral-variant-60 dark:text-neutral-variant-40">
                {emptyMessage ?? "No members available"}
              </p>
            </div>
          ) : (
            options.map((option) => (
              <ComboboxOption
                key={option.id}
                value={option}
                as="div"
                onClick={() => toggleOption(option)}
                className="group flex h-auto cursor-pointer select-none items-center border-b border-gray-600 bg-surface-container-low last:border-0 data-[focus]:bg-surface-container dark:border-neutral-variant-40 dark:bg-surface-container-low-dark"
              >
                <div className="flex size-full items-center gap-2 border-l-3 border-transparent p-5 pl-3 hover:border-info-80">
                  <CheckIcon
                    className={twMerge(
                      "size-4 text-neutral-variant-20 dark:text-primary-98",
                      value.includes(option.id) ? "visible" : "invisible",
                    )}
                    strokeWidth={3}
                  />
                  <div className="text-sm/6 text-neutral-variant-20 dark:text-primary-98">
                    {option.label}
                  </div>
                </div>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
    </Field>
  );
};
